/**
 * The ONE player instance, as React state (SPEC_CAPTURE_UX_R2 §3.1 "one player instance").
 *
 * A module singleton with `useSyncExternalStore`, the pattern this app already uses for the grid toggle
 * (`viewfinderPrefs`) and low-data mode (`lowData`) — no provider to forget, so a screen rendered on its own (a
 * test, a future embed) gets the same player and the controls are never dead.
 *
 * Mute is persisted PER LINK (§3.1, SPEC_AUDIO_GUIDANCE §3.2: `localStorage['vcip-svapp-guidance:<caseRef>']`),
 * default unmuted; the link key never contains the link token itself (`linkKey.ts`).
 */

import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { GuidancePlayer, type GuidanceCue, type GuidanceState, type PlayerDeps } from './GuidancePlayer';
import { linkScopedKey } from './linkKey';
import { warmVoices } from './speech';

const MUTE_PREFIX = 'vcip-svapp-guidance';

export function mutedKey(): string {
  return linkScopedKey(MUTE_PREFIX);
}

export function readMutedPref(): boolean {
  try {
    return window.localStorage.getItem(mutedKey()) === 'muted';
  } catch {
    return false;
  }
}

function writeMutedPref(muted: boolean): void {
  try {
    if (muted) window.localStorage.setItem(mutedKey(), 'muted');
    else window.localStorage.removeItem(mutedKey());
  } catch {
    /* a device that refuses storage still honours the choice for this session */
  }
}

export interface GuidanceView {
  readonly state: GuidanceState;
  /** The cue the screen registered — what the replay control replays. */
  readonly cue: GuidanceCue | null;
}

let player: GuidancePlayer | null = null;
let cue: GuidanceCue | null = null;
let view: GuidanceView | null = null;
const listeners = new Set<() => void>();
/** Cues already auto-played this session — the auto-play is once per step, replay is unlimited. */
const autoPlayed = new Set<string>();
/**
 * FSD-20 Addendum C: the instruction card's rung-3 note ("Audio guidance is not available in <language> yet") is said
 * ONCE per session and language — on the step that first reached rung 3 — so later cards don't spend two lines over
 * the camera repeating it. Language → the cue key that owns it.
 */
const noteOwners = new Map<string, string>();

/** True when the card for `cueKey` may show the rung-3 note in `locale`: the first card to ask owns it. Idempotent. */
export function ownsGuidanceNote(locale: string, cueKey: string): boolean {
  const owner = noteOwners.get(locale);
  if (owner === undefined) {
    noteOwners.set(locale, cueKey);
    return true;
  }
  return owner === cueKey;
}

/** A cue's auto-play identity: its key AND its locale, so switching language mid-step speaks the step again in the
 *  new language (FSD-18 D1) instead of falling silent because the key was already played. */
function playedId(c: Pick<GuidanceCue, 'key' | 'locale'>): string {
  return `${c.key}|${c.locale}`;
}

function publish(): void {
  view = { state: guidancePlayer().snapshot(), cue };
  for (const l of listeners) l();
}

export function guidancePlayer(): GuidancePlayer {
  if (!player) {
    player = new GuidancePlayer();
    player.subscribe(() => {
      view = { state: (player as GuidancePlayer).snapshot(), cue };
      for (const l of listeners) l();
    });
    if (readMutedPref()) player.setMuted(true);
    // The voice list loads asynchronously on iOS — ask as soon as the app has a player.
    warmVoices();
    hookPageExit();
  }
  return player;
}

let pageHooked = false;
/** Once per page (whichever player is current): leaving or hiding the page stops the read. */
function hookPageExit(): void {
  if (pageHooked) return;
  pageHooked = true;
  // Closing / leaving the page (or the bfcache freezing it) must not leave the phone talking.
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('pagehide', () => player?.cancel());
  }
  // Nor may a page that is only HIDDEN — another app, the screen locked, a tab switch (no pagehide fires): a device
  // voice can keep reading in the background. Stop, and never resume on its own (the audio rung's duck rule).
  if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') player?.cancel();
    });
  }
}

function snapshot(): GuidanceView {
  if (!view) view = { state: guidancePlayer().snapshot(), cue };
  return view;
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** The evidence interlock, from the clip screen: true from `start-recording` until the clip is decided. */
export function setGuidanceRecording(recording: boolean): void {
  guidancePlayer().setRecording(recording);
}

export function setGuidanceCue(next: GuidanceCue | null): void {
  cue = next;
  publish();
}

export interface GuidanceApi extends GuidanceView {
  /** Called inside a user gesture (the "turn on the camera" tap, the replay button, "Listen to this notice"). */
  readonly unlock: () => void;
  /** Replay the registered cue. Unlocks first, so the control is never dead before the camera is on. */
  readonly replay: () => void;
  readonly setMuted: (muted: boolean) => void;
  readonly toggleMuted: () => void;
  readonly playSections: (cues: readonly GuidanceCue[]) => void;
  readonly stop: () => void;
}

export function useGuidance(): GuidanceApi {
  const v = useSyncExternalStore(subscribe, snapshot, snapshot);
  const unlock = useCallback(() => guidancePlayer().unlock(), []);
  const replay = useCallback(() => {
    const p = guidancePlayer();
    const current = snapshot().cue;
    // A replay IS this step's play: record it before unlocking, so the unlock does not ALSO trigger the auto-play
    // and speak the same words twice.
    if (current) autoPlayed.add(playedId(current));
    p.unlock();
    if (current) void p.play(current);
  }, []);
  const setMuted = useCallback((muted: boolean) => {
    writeMutedPref(muted);
    guidancePlayer().setMuted(muted);
  }, []);
  const toggleMuted = useCallback(() => {
    const next = !snapshot().state.muted;
    writeMutedPref(next);
    guidancePlayer().setMuted(next);
  }, []);
  const playSections = useCallback((cues: readonly GuidanceCue[]) => {
    const p = guidancePlayer();
    p.unlock();
    void p.playSections(cues);
  }, []);
  const stop = useCallback(() => guidancePlayer().cancel(), []);
  return { ...v, unlock, replay, setMuted, toggleMuted, playSections, stop };
}

/**
 * Register the cue for the screen that is showing, and auto-play it ONCE (§3.1 "auto-plays once per step, after
 * the first user gesture"). A step change cancels any in-flight clip on the way out; a cue is only marked played
 * when an attempt was actually made, so the unlock gesture still speaks the step the customer is standing on.
 */
export function useGuidanceCue(next: GuidanceCue | null): void {
  const key = next?.key ?? null;
  const text = next?.text ?? '';
  const locale = next?.locale ?? '';
  const unlocked = useSyncExternalStore(subscribe, snapshot, snapshot).state.unlocked;
  // biome-ignore lint/correctness/useExhaustiveDependencies: the cue is identified by key+text+locale, never by object identity
  useEffect(() => {
    setGuidanceCue(next);
    if (!next || !key) return undefined;
    const p = guidancePlayer();
    const s = p.snapshot();
    if (unlocked && !s.muted && !s.recording && !autoPlayed.has(playedId(next))) {
      autoPlayed.add(playedId(next));
      void p.play(next);
    }
    return () => {
      // A step change (or leaving the screen) cancels whatever is still in flight.
      p.cancel();
    };
  }, [key, text, locale, unlocked]);
}

export function resetGuidanceForTests(deps?: PlayerDeps): void {
  player?.dispose();
  player = deps ? new GuidancePlayer(deps) : null;
  if (player) {
    const p = player;
    p.subscribe(() => {
      view = { state: p.snapshot(), cue };
      for (const l of listeners) l();
    });
  }
  cue = null;
  view = null;
  autoPlayed.clear();
  noteOwners.clear();
  try {
    window.localStorage.removeItem(mutedKey());
  } catch {
    /* ignore */
  }
  publish();
}
